import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useRouter } from 'next/router';
import { Tooltip } from '@nextui-org/react';
import { useAuthContext } from '@/context/AuthContext';
import { createClient } from '@/lib/supabase/client';

// Create a context
const TourContext = createContext();

// Tour provider component
export const TourProvider = ({ children }) => {
  // Get session and user from AuthContext
  const { user, session } = useAuthContext();
  const supabase = createClient();

  // Track which tours the user has seen locally (fallback)
  const [localToursViewed, setLocalToursViewed] = useLocalStorage('zenyai-tours-viewed', {
    dashboard: false,
    soundGenerator: false,
    projects: false,
    appIntro: false,
    chat: false,
    whiteboard: false,
    assets: false,
    profile: false,
    editor: false,
    fullApp: false
  });

  // Derived state combining local storage and user metadata
  const [toursViewed, setToursViewed] = useState(localToursViewed);

  // Sync with user metadata when session is available
  useEffect(() => {
    if (user && session?.user?.user_metadata?.tours_completed) {
      setToursViewed(prev => ({
        ...prev,
        ...session.user.user_metadata.tours_completed
      }));
    } else {
      setToursViewed(localToursViewed);
    }
  }, [user, session, localToursViewed]);

  // Track if any tour is currently active
  const [activeTour, setActiveTour] = useState(null);

  // Track if tours are ready to be shown (used to coordinate with page transitions)
  const [toursReady, setToursReady] = useState(false);

  // Automatically start the dashboard tour logic
  useEffect(() => {
    // Only show the tour if:
    // 1. Tours are ready
    // 2. No tour is currently active
    // 3. User hasn't viewed the dashboard tour
    // 4. User exists (we only tour logged in users usually)
    if (toursReady && !activeTour && !toursViewed.dashboard && user) {
      // Small delay to ensure UI is ready
      const timer = setTimeout(() => {
        startTour('dashboard');
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [toursReady, activeTour, toursViewed.dashboard, user]);

  // Editor Tour Trigger
  const router = useRouter();
  useEffect(() => {
    if (router.pathname.includes('/editor') && !activeTour && toursReady && !toursViewed.editor) {
      // Force run editor tour for testing
      startTour('editor');
    }
  }, [router.pathname, activeTour, toursReady, toursViewed.editor]);

  // Set tours ready after a short delay to allow page transitions to complete
  useEffect(() => {
    const timer = setTimeout(() => {
      setToursReady(true);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Methods to control tours
  const startTour = (tourName) => {
    setActiveTour(tourName);
  };

  const endTour = (tourName) => {
    if (activeTour === tourName) {
      setActiveTour(null);
    }

    // Mark this tour as viewed
    const newToursViewed = {
      ...toursViewed,
      [tourName]: true
    };

    // Update local state
    setToursViewed(newToursViewed);
    setLocalToursViewed(prev => ({
      ...prev,
      [tourName]: true
    }));

    // Update Supabase metadata
    if (session?.user) {
      supabase.auth.updateUser({
        data: {
          tours_completed: newToursViewed
        }
      }).catch(err => {
        // console.error('Failed to update tour status', err);
      });
    }

    // If this was the dashboard tour, we also want to mark them as "onboarded" if not already
    // This is a failsafe in case they skipped the main onboarding
    if (tourName === 'dashboard') {
      // Optional: markUserAsOnboarded(); 
    }
  };

  const resetAllTours = () => {
    setToursViewed({
      dashboard: false,
      soundGenerator: false,
      projects: false,
      appIntro: false,
      chat: false,
      whiteboard: false,
      assets: false,
      profile: false,
      fullApp: false
    });
    setActiveTour(null);
  };



  // Check if a specific tour has been viewed
  const hasTourBeenViewed = (tourName) => {
    return toursViewed[tourName] || false;
  };

  // Context value
  const value = {
    toursViewed,
    activeTour,
    toursReady,
    startTour,
    endTour,
    resetAllTours,
    hasTourBeenViewed,
    setToursReady

  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

// Custom hook to use the tour context
export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export default TourContext; 